/**
 * Baumlogik für verschachtelte Ordner.
 *
 * Wird vom Downloadbereich (DownloadsTreeView) und vom Aktenbaum der
 * Fallakte (DocsTreeView) gemeinsam genutzt. Die Regeln spiegeln das
 * Backend-Paket `internal/foldertree`: höchstens drei Ebenen, keine Zyklen,
 * ein Ordner ist nie sein eigener Vorfahre. Reine Funktionen — ohne Vue —
 * damit sie sich direkt testen lassen.
 */

/** Erlaubte Ebenen: Wurzel = 1, Unterordner = 2, dessen Unterordner = 3. */
export const MAX_FOLDER_DEPTH = 3

/** Trennzeichen im Pfad-Label ("Haftpflicht / Gutachten"). */
export const PATH_SEPARATOR = ' / '

export interface TreeNode<T> {
  item: T
  key: string
  parentKey: string | null
  /** 0 für Wurzelknoten. */
  depth: number
  /** Schlüssel aller Vorfahren, der direkte Elternknoten zuerst. */
  ancestorKeys: string[]
  children: TreeNode<T>[]
}

export interface TreeAccessors<T> {
  keyOf: (item: T) => string
  parentKeyOf: (item: T) => string | null
}

interface TreeIndex<T> {
  byKey: Map<string, T>
  /** Elternschlüssel je Knoten — nach Bereinigung: unbekannte Eltern → null. */
  parentOf: Map<string, string | null>
  childrenOf: Map<string | null, string[]>
}

/**
 * Index über die Ordnerliste. Ein parentId, das auf einen unbekannten
 * (z. B. per Rechtevergabe ausgeblendeten) Ordner zeigt, wird wie "keine
 * Eltern" behandelt — so bleibt jeder Ordner erreichbar.
 */
function indexTree<T> (items: T[], { keyOf, parentKeyOf }: TreeAccessors<T>): TreeIndex<T> {
  const byKey = new Map<string, T>()
  for (const item of items) {
    byKey.set(keyOf(item), item)
  }

  const parentOf = new Map<string, string | null>()
  for (const item of items) {
    const key = keyOf(item)
    const rawParent = parentKeyOf(item)
    parentOf.set(key, rawParent !== null && rawParent !== key && byKey.has(rawParent) ? rawParent : null)
  }

  // Zyklen in Altdaten (A → B → A) hätten keinen Wurzelknoten und würden
  // komplett verschwinden. Der Knoten, an dem der Zyklus erkannt wird, wird
  // auf die Wurzelebene gehoben — so bleibt jeder Ordner erreichbar.
  for (const key of parentOf.keys()) {
    const seen = new Set<string>([key])
    let current = parentOf.get(key) ?? null
    while (current !== null) {
      if (seen.has(current)) {
        parentOf.set(key, null)
        break
      }
      seen.add(current)
      current = parentOf.get(current) ?? null
    }
  }

  const childrenOf = new Map<string | null, string[]>()
  for (const item of items) {
    const key = keyOf(item)
    const parent = parentOf.get(key) ?? null
    const siblings = childrenOf.get(parent) ?? []
    childrenOf.set(parent, [...siblings, key])
  }
  return { byKey, parentOf, childrenOf }
}

/** Vorfahren eines Knotens, der direkte Elternknoten zuerst. Zyklen brechen ab. */
function ancestorsFromIndex<T> (index: TreeIndex<T>, key: string): string[] {
  const out: string[] = []
  const seen = new Set<string>([key])
  let current = index.parentOf.get(key) ?? null
  while (current !== null && !seen.has(current)) {
    seen.add(current)
    out.push(current)
    current = index.parentOf.get(current) ?? null
  }
  return out
}

/**
 * Verschachtelte Baumstruktur in der Reihenfolge der Eingabeliste. Knoten,
 * deren Eltern unbekannt sind, stehen auf der Wurzelebene.
 */
export function buildTree<T> (items: T[], accessors: TreeAccessors<T>): TreeNode<T>[] {
  const index = indexTree(items, accessors)
  const build = (parentKey: string | null, depth: number, ancestorKeys: string[], seen: Set<string>): TreeNode<T>[] =>
    (index.childrenOf.get(parentKey) ?? [])
      .filter(key => !seen.has(key))
      .map(key => {
        const nextSeen = new Set(seen).add(key)
        return {
          item: index.byKey.get(key) as T,
          key,
          parentKey,
          depth,
          ancestorKeys,
          children: build(key, depth + 1, [key, ...ancestorKeys], nextSeen),
        }
      })
  return build(null, 0, [], new Set())
}

/** Tiefensuche: Eltern vor Kindern — die Reihenfolge, in der der Baum rendert. */
export function flattenTree<T> (nodes: TreeNode<T>[]): TreeNode<T>[] {
  return nodes.flatMap(node => [node, ...flattenTree(node.children)])
}

/** Alle Nachfahren-Schlüssel eines Ordners (Tiefensuche). */
export function descendantKeys<T> (items: T[], accessors: TreeAccessors<T>, key: string): string[] {
  const index = indexTree(items, accessors)
  const out: string[] = []
  const seen = new Set<string>([key])
  const walk = (current: string) => {
    for (const child of index.childrenOf.get(current) ?? []) {
      if (seen.has(child)) {
        continue
      }
      seen.add(child)
      out.push(child)
      walk(child)
    }
  }
  walk(key)
  return out
}

/** Ebene eines Ordners: 1 für Wurzelordner, 0 für unbekannte Schlüssel. */
export function depthOf<T> (items: T[], accessors: TreeAccessors<T>, key: string): number {
  const index = indexTree(items, accessors)
  if (!index.byKey.has(key)) {
    return 0
  }
  return 1 + ancestorsFromIndex(index, key).length
}

/** Anzahl Ebenen des Teilbaums unter key, den Ordner selbst eingeschlossen. */
export function subtreeHeight<T> (items: T[], accessors: TreeAccessors<T>, key: string): number {
  const index = indexTree(items, accessors)
  const own = ancestorsFromIndex(index, key).length
  return descendantKeys(items, accessors, key).reduce(
    (max, child) => Math.max(max, ancestorsFromIndex(index, child).length - own + 1),
    1,
  )
}

/**
 * Pfad-Namen eines Ordners von der Wurzel bis zu ihm selbst
 * (["Haftpflicht", "Gutachten"]).
 */
export function folderPath<T> (
  items: T[],
  accessors: TreeAccessors<T>,
  key: string,
  nameOf: (item: T) => string,
): string[] {
  const index = indexTree(items, accessors)
  const self = index.byKey.get(key)
  if (!self) {
    return []
  }
  // Vorfahren kommen "nächster zuerst" — für den Pfad von der Wurzel her
  // werden sie vorn eingefügt (kein toReversed: TS-Lib-Ziel ist älter als ES2023).
  const names: string[] = []
  for (const ancestor of ancestorsFromIndex(index, key)) {
    names.unshift(nameOf(index.byKey.get(ancestor) as T))
  }
  names.push(nameOf(self))
  return names
}

/** Pfad als Anzeigetext ("Haftpflicht / Gutachten"). */
export function folderPathLabel<T> (
  items: T[],
  accessors: TreeAccessors<T>,
  key: string,
  nameOf: (item: T) => string,
): string {
  return folderPath(items, accessors, key, nameOf).join(PATH_SEPARATOR)
}

/**
 * Ordner, unter die ein Ordner (movingKey) verschoben bzw. ein neuer Ordner
 * (movingKey null) angelegt werden darf: nicht der Ordner selbst, keiner
 * seiner Nachfahren, und die Tiefe bleibt im Rahmen. Reihenfolge wie der
 * gerenderte Baum.
 */
export function eligibleParents<T> (
  items: T[],
  accessors: TreeAccessors<T>,
  movingKey: string | null,
  maxDepth: number = MAX_FOLDER_DEPTH,
): T[] {
  const excluded = new Set<string>(movingKey === null ? [] : [movingKey, ...descendantKeys(items, accessors, movingKey)])
  const height = movingKey === null ? 1 : subtreeHeight(items, accessors, movingKey)
  return flattenTree(buildTree(items, accessors))
    .filter(node => !excluded.has(node.key) && node.depth + 1 + height <= maxDepth)
    .map(node => node.item)
}

/** Darf unter diesem Knoten (0-basierte Tiefe) noch ein Unterordner entstehen? */
export function canCreateChildAt (depth: number, maxDepth: number = MAX_FOLDER_DEPTH): boolean {
  return depth + 2 <= maxDepth
}
